
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLanguage } from '@/contexts/LanguageContext';
import SentimentAnalysis from './insights/SentimentAnalysis';
import RecommendationsView from './insights/RecommendationsView';
import PredictionView from './insights/PredictionView';
import CompetitiveAnalysis from './insights/CompetitiveAnalysis';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { ScoreBreakdown } from '@/types/survey';
import { Question } from '@/types/survey';
import MethodologySection from './MethodologySection';
import { Sparkles, MessageSquare, TrendingUp, Award, Book, ChartBar } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AIInsightsProps {
  scoreBreakdown: ScoreBreakdown | null;
  responses: Record<string, any>;
  industry: string;
  questions: Question[];
}

const AIInsights: React.FC<AIInsightsProps> = ({
  scoreBreakdown,
  responses,
  industry,
  questions
}) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = React.useState('sentiment');
  
  // Debug logs for troubleshooting
  React.useEffect(() => {
    if (!scoreBreakdown) {
      console.log("No score breakdown available");
    } else {
      console.log("Score breakdown available:", {
        totalNEV: scoreBreakdown.totalNEV,
        recommendations: scoreBreakdown.recommendations,
        categories: scoreBreakdown.categories
      });
    }
  }, [scoreBreakdown]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-bold text-blue-600 flex items-center">
                <Sparkles className="mr-2 h-5 w-5 text-blue-500" />
                {t('results.beamAIInsights')}
              </CardTitle>
              <CardDescription className="text-gray-500 mt-1">
                {t('ai.insights.description') || 'AI-powered analysis of your project\'s emotional and strategic value'}
              </CardDescription>
            </div>
            <span className="text-xs font-normal bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
              {t('ai.insights.beta')}
            </span>
          </div>
        </CardHeader>
      </Card>

      <Tabs 
        defaultValue={activeTab} 
        className="space-y-4"
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger 
            value="sentiment" 
            className="text-xs flex items-center"
            onClick={() => setActiveTab('sentiment')}
          >
            <MessageSquare className="mr-1 h-3.5 w-3.5" />
            {t('ai.sentiment.analysis')}
          </TabsTrigger>
          <TabsTrigger 
            value="recommendations" 
            className="text-xs flex items-center"
            onClick={() => setActiveTab('recommendations')}
          >
            <Award className="mr-1 h-3.5 w-3.5" />
            {t('results.recommendations')}
          </TabsTrigger>
          <TabsTrigger 
            value="prediction" 
            className="text-xs flex items-center"
            onClick={() => setActiveTab('prediction')}
          >
            <TrendingUp className="mr-1 h-3.5 w-3.5" />
            {t('ai.prediction.future')}
          </TabsTrigger>
          <TabsTrigger 
            value="competitive" 
            className="text-xs flex items-center"
            onClick={() => setActiveTab('competitive')}
          >
            <ChartBar className="mr-1 h-3.5 w-3.5" />
            {t('ai.competitive.analysis')}
          </TabsTrigger>
          <TabsTrigger 
            value="methodology" 
            className="text-xs flex items-center"
            onClick={() => setActiveTab('methodology')}
          >
            <Book className="mr-1 h-3.5 w-3.5" />
            {t('results.beamMethodology')}
          </TabsTrigger>
        </TabsList>

        {!scoreBreakdown && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>
              {t('ai.insights.noData') || 'No score data available. Complete the assessment to view AI insights.'}
            </AlertDescription>
          </Alert>
        )}

        <TabsContent value="sentiment">
          <SentimentAnalysis 
            scoreBreakdown={scoreBreakdown} 
            responses={responses} 
            questions={questions} 
          />
        </TabsContent>

        <TabsContent value="recommendations">
          <RecommendationsView 
            scoreBreakdown={scoreBreakdown} 
            industry={industry} 
          />
        </TabsContent>

        <TabsContent value="prediction">
          <PredictionView 
            scoreBreakdown={scoreBreakdown} 
          />
        </TabsContent>

        <TabsContent value="competitive">
          <CompetitiveAnalysis 
            scoreBreakdown={scoreBreakdown} 
            industry={industry} 
          />
        </TabsContent>

        <TabsContent value="methodology">
          <MethodologySection />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIInsights;
